<template>
  <section class="signup-page">
    <div class="signup-shell">
      <main class="signup-panel" aria-labelledby="signup-title">
        <div class="panel-header">
          <p class="panel-kicker">Try 10 calls free</p>
          <h1 id="signup-title">Launch your AI receptionist today.</h1>
          <p>
            Create your workspace, test real call handling, and see how many
            missed calls can turn into bookings and payments.
          </p>
        </div>

        <div class="offer-strip">
          <CheckCircle2 :size="18" stroke-width="2.2" />
          <span>No credit card required to start. 10 free calls included.</span>
        </div>

        <div class="social-login">
          <button class="social-btn google" type="button" @click="loginWithGoogle">
            <svg class="social-logo" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.24 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.3 9.14 5.38 12 5.38z" />
            </svg>
            Continue with Google
          </button>

          <button class="social-btn facebook" type="button" @click="loginWithFacebook">
            <svg class="social-logo" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#1877F2" d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.96h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
            </svg>
            Continue with Facebook
          </button>
        </div>

        <div class="divider">
          <span>or create an account with email</span>
        </div>

        <form class="signup-form" @submit.prevent="handleSubmit">
          <label for="name">Full name</label>
          <div class="field">
            <UserRound :size="18" stroke-width="2.2" />
            <input
              id="name"
              v-model.trim="form.name"
              type="text"
              autocomplete="name"
              placeholder="Sarah Johnson"
              required
            />
          </div>

          <label for="email">Work email</label>
          <div class="field">
            <Mail :size="18" stroke-width="2.2" />
            <input
              id="email"
              v-model.trim="form.email"
              type="email"
              autocomplete="email"
              placeholder="you@company.com"
              required
            />
          </div>

          <label for="password">Password</label>
          <div class="field">
            <LockKeyhole :size="18" stroke-width="2.2" />
            <input
              id="password"
              v-model="form.password"
              type="password"
              autocomplete="new-password"
              placeholder="Create a secure password"
              required
            />
          </div>

          <p v-if="error" class="error-message" role="alert">{{ error }}</p>

          <button type="submit" class="signup-button" :disabled="loading">
            <span>{{ loading ? 'Creating workspace...' : 'Start with 10 free calls' }}</span>
            <ArrowRight :size="18" stroke-width="2.2" />
          </button>
        </form>

        <p class="signin-link">
          Already have an account?
          <router-link to="/sign-in">Sign in</router-link>
        </p>
      </main>

      <aside class="conversion-panel">
        <p class="eyebrow">Built for service businesses</p>
        <h2>One assistant for calls, bookings, payments, chatbot, and a quick website.</h2>
        <p class="lead">
          NoCodeAgent helps teams answer when staff are busy, collect the right
          information, book the appointment, and collect a partial or full
          payment by phone when needed.
        </p>

        <div class="value-grid">
          <div class="value-card">
            <PhoneCall :size="20" stroke-width="2.2" />
            <strong>Answer missed calls</strong>
            <span>Capture demand after hours or when staff are overloaded.</span>
          </div>
          <div class="value-card">
            <CalendarCheck :size="20" stroke-width="2.2" />
            <strong>Book appointments</strong>
            <span>Collect date, time, service, name, and phone.</span>
          </div>
          <div class="value-card">
            <CreditCard :size="20" stroke-width="2.2" />
            <strong>Take payments</strong>
            <span>Support deposits, partial payments, or full payments by phone.</span>
          </div>
          <div class="value-card">
            <MessagesSquare :size="20" stroke-width="2.2" />
            <strong>Add website chat</strong>
            <span>Embed the chatbot into an existing website easily.</span>
          </div>
          <div class="value-card">
            <Globe2 :size="20" stroke-width="2.2" />
            <strong>Create a website</strong>
            <span>Launch a simple web presence quickly if they do not have one.</span>
          </div>
          <div class="value-card">
            <MessageSquareText :size="20" stroke-width="2.2" />
            <strong>Follow up by SMS</strong>
            <span>Send confirmations and next steps automatically.</span>
          </div>
        </div>

        <div class="price-card">
          <span>After the trial</span>
          <strong>100 calls for $250</strong>
          <p>Continue after the first 10 free calls with clear upgrade room as volume grows.</p>
        </div>
      </aside>
    </div>
  </section>
</template>
<script setup>
import { computed, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  ArrowRight,
  CalendarCheck,
  CheckCircle2,
  CreditCard,
  Globe2,
  LockKeyhole,
  Mail,
  MessageSquareText,
  MessagesSquare,
  PhoneCall,
  UserRound
} from 'lucide-vue-next'
import Cookies from 'js-cookie'
import { auth } from '@/services/api.js'

const route = useRoute()
const router = useRouter()
const loading = ref(false)
const error = ref('')

const form = reactive({
  name: '',
  email: '',
  password: ''
})

const nextUrl = computed(() => {
  return route.query.next ? String(route.query.next) : '/app/dashboard'
})

const handleSubmit = async () => {
  try {
    loading.value = true
    error.value = ''

    let response = await auth.register({
      name: form.name,
      email: form.email,
      password: form.password
    })

    let token = response?.token

    if (!token) {
      response = await auth.login({
        email: form.email,
        password: form.password
      })

      token = response?.token
    }

    if (!token) {
      error.value = 'Account created, but sign-in failed. Please sign in manually.'
      return
    }

    Cookies.set('token', token, {
      secure: true,
      sameSite: 'Strict'
    })

    if (response.user?.name || form.name) localStorage.setItem('userName', response.user?.name || form.name)
    if (response.user?.email || form.email) localStorage.setItem('userEmail', response.user?.email || form.email)

    await router.push(nextUrl.value)
  } catch (err) {
    console.error('Signup error:', err)
    error.value = 'Unable to create your account. Please try again.'
  } finally {
    loading.value = false
  }
}

function loginWithGoogle() {
  window.location.href = 'https://nocodeagent.ai/api/auth/google'
}

function loginWithFacebook() {
  window.location.href = 'https://nocodeagent.ai/api/auth/facebook'
}
</script>
<style scoped>
.signup-page {
  min-height: calc(100vh - 144px);
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(circle at top left, rgba(16, 185, 129, 0.16), transparent 34%),
    linear-gradient(135deg, #fbfaf7 0%, #eef7f1 44%, #f6f0e8 100%);
  padding: 32px 20px;
  color: #17211b;
}

.signup-shell {
  width: min(1180px, 100%);
  display: grid;
  grid-template-columns: minmax(390px, 0.88fr) minmax(0, 1.12fr);
  border: 1px solid rgba(23, 33, 27, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 24px 70px rgba(34, 45, 38, 0.16);
  overflow: hidden;
}

.signup-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 52px;
  background: #ffffff;
}

.panel-header {
  margin-bottom: 18px;
}

.panel-kicker,
.eyebrow {
  margin: 0 0 10px;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.panel-kicker {
  color: #0d8f62;
}

.panel-header h1 {
  margin: 0;
  color: #17211b;
  font-size: clamp(2rem, 3vw, 3.25rem);
  line-height: 1.02;
  letter-spacing: 0;
}

.panel-header p {
  margin: 14px 0 0;
  color: #66736b;
  font-size: 1rem;
  line-height: 1.6;
}

.offer-strip {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding: 12px 14px;
  border: 1px solid rgba(13, 143, 98, 0.16);
  border-radius: 8px;
  background: rgba(13, 143, 98, 0.08);
  color: #135c43;
  font-size: 0.92rem;
  font-weight: 800;
}

.offer-strip svg {
  flex: 0 0 auto;
}

.social-login {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.social-btn,
.signup-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 48px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.social-btn:hover,
.signup-button:hover:not(:disabled) {
  transform: translateY(-1px);
}

.social-logo {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}

.google {
  background: #17211b;
  color: #ffffff;
}

.facebook {
  background: #ffffff;
  color: #17211b;
  border-color: #dde5df;
}

.divider {
  position: relative;
  margin: 24px 0;
  text-align: center;
}

.divider::before {
  content: '';
  position: absolute;
  inset: 50% 0 auto;
  height: 1px;
  background: #e6ece8;
}

.divider span {
  position: relative;
  z-index: 1;
  padding: 0 12px;
  background: #ffffff;
  color: #7a8780;
  font-size: 0.84rem;
  font-weight: 700;
}

.signup-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.signup-form label {
  color: #29352f;
  font-size: 0.9rem;
  font-weight: 800;
}

.field {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 50px;
  padding: 0 14px;
  border: 1px solid #dce5df;
  border-radius: 8px;
  background: #fbfdfb;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.field:focus-within {
  border-color: #18a16f;
  box-shadow: 0 0 0 4px rgba(24, 161, 111, 0.12);
}

.field svg {
  flex: 0 0 auto;
  color: #6b7a71;
}

.field input {
  width: 100%;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: #17211b;
  font: inherit;
}

.field input::placeholder {
  color: #9aa6a0;
}

.error-message {
  margin: 6px 0 0;
  padding: 12px 14px;
  border: 1px solid rgba(185, 28, 28, 0.18);
  border-radius: 8px;
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
  font-size: 0.9rem;
  line-height: 1.4;
}

.signup-button {
  width: 100%;
  margin-top: 12px;
  background: #0d8f62;
  color: #ffffff;
  box-shadow: 0 14px 30px rgba(13, 143, 98, 0.2);
}

.signup-button:disabled {
  cursor: not-allowed;
  opacity: 0.68;
  box-shadow: none;
}

.signin-link {
  margin: 20px 0 0;
  color: #66736b;
  text-align: center;
}

.signin-link a {
  color: #0d8f62;
  font-weight: 800;
  text-decoration: none;
}

.signin-link a:hover {
  text-decoration: underline;
}

.conversion-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 22px;
  padding: 52px;
  background:
    linear-gradient(150deg, rgba(24, 91, 74, 0.96), rgba(25, 39, 33, 0.98)),
    #1f3f35;
  color: #ffffff;
}

.eyebrow {
  color: #b8f5d6;
}

.conversion-panel h2 {
  max-width: 660px;
  margin: 0;
  font-size: clamp(2rem, 3.4vw, 4rem);
  line-height: 1;
  letter-spacing: 0;
}

.lead {
  max-width: 620px;
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 1.03rem;
  line-height: 1.7;
}

.value-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.value-card {
  min-height: 118px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
}

.value-card svg {
  color: #8ff0bd;
}

.value-card strong {
  display: block;
  margin-top: 10px;
  color: #ffffff;
  font-size: 0.96rem;
}

.value-card span {
  display: block;
  margin-top: 6px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.86rem;
  line-height: 1.45;
}

.price-card {
  padding: 18px;
  border: 1px solid rgba(143, 240, 189, 0.22);
  border-radius: 8px;
  background: rgba(143, 240, 189, 0.1);
}

.price-card span {
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.82rem;
  font-weight: 800;
  text-transform: uppercase;
}

.price-card strong {
  display: block;
  margin-top: 6px;
  color: #ffffff;
  font-size: 2rem;
}

.price-card p {
  margin: 6px 0 0;
  color: rgba(255, 255, 255, 0.74);
  line-height: 1.5;
}

@media (max-width: 980px) {
  .signup-shell {
    grid-template-columns: 1fr;
  }

  .conversion-panel {
    order: -1;
    padding: 34px;
  }
}

@media (max-width: 640px) {
  .signup-page {
    min-height: calc(100vh - 56px);
    align-items: flex-start;
    padding: 0;
    background: #ffffff;
  }

  .signup-shell {
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  .conversion-panel {
    display: none;
  }

  .signup-panel {
    min-height: calc(100vh - 56px);
    justify-content: flex-start;
    padding: 22px 20px 28px;
  }

  .panel-header {
    margin-bottom: 14px;
  }

  .panel-kicker {
    margin-bottom: 8px;
  }

  .panel-header h1 {
    font-size: 1.72rem;
  }

  .panel-header p {
    margin-top: 8px;
    font-size: 0.92rem;
  }

  .offer-strip {
    margin-bottom: 14px;
    padding: 10px 12px;
    font-size: 0.86rem;
  }

  .social-login {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .social-btn,
  .signup-button,
  .field {
    min-height: 44px;
  }

  .divider {
    margin: 14px 0;
  }

  .signup-form {
    gap: 8px;
  }

  .signup-button {
    margin-top: 10px;
  }

  .signin-link {
    margin-top: 14px;
  }
}
</style>
